// subscription-info.tsx
import React from 'react';
import { Card, Flex, Space, Typography, Tag, Divider } from 'antd';
import { useStore } from '../../helpers/use-store';
import { SubscriptionStatusName } from '@monorepo/types';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import SubscriptionActions from './subscription-actions';
import {
  CrownOutlined,
  ThunderboltOutlined,
  RocketOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';

import './subscription-info.scss';
import { useTranslation } from 'react-i18next';

const { Text, Title } = Typography;

const getStatusColor = (status?: SubscriptionStatusName) => {
  switch (status) {
    case SubscriptionStatusName.Active:
      return 'success';
    case SubscriptionStatusName.Suspended:
    case SubscriptionStatusName.Terminated:
    case SubscriptionStatusName.Finished:
      return 'default';
    default:
      return 'default';
  }
};

const SubscriptionInfo = () => {
  const { t } = useTranslation();

  const getDateLabel = (status?: SubscriptionStatusName) => {
    switch (status) {
      case SubscriptionStatusName.Active:
        return t('billing.subscription.nextBillingDate');
      default:
        return t('billing.subscription.nextBillingDate');
    }
  };
  const {
    dataStore: { userStore, planStore },
  } = useStore();
  const subscription = userStore.userSubscription;

  const {
    credits,
    topupCredits = 0,
    allowedSeriesAmount,
    status,
    nextChargeDate,
    planId,
  } = subscription;

  const plan = planStore.get(planId || '');

  const isActive = status === SubscriptionStatusName.Active;
  const isBeforeRenewal = dayjs().isBefore(dayjs(nextChargeDate));
  const isSuspended = status === SubscriptionStatusName.Suspended;

  return (
    <Card className="subscription-info-card">
      {/* Header */}
      <div className="subscription-header">
        <Flex align="center" justify="space-between">
          <Flex align="center" gap={12}>
            <div className={`status-icon ${isActive ? 'active' : 'inactive'}`}>
              {isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            </div>
            <div>
              <Title level={4} style={{ margin: 0 }}>
                {t('billing.subscription.title')}
              </Title>
              <Tag
                color={getStatusColor(status)}
                style={{ marginTop: '4px' }}
                icon={isActive ? <CheckCircleOutlined /> : undefined}
              >
                {isActive
                  ? t('billing.subscription.statuses.active')
                  : t('billing.subscription.statuses.inactive')}
              </Tag>
            </div>
          </Flex>
          {/* PayPro users have no working management actions — the actions
              menu would route to a dead provider. Show nothing; the plan
              cards below become the only path forward (re-subscribe via
              Polar). */}
          {!userStore.isPayproLocked && <SubscriptionActions />}
        </Flex>

        {isSuspended && (
          <div className="suspended-notice">
            <Text type="secondary">
              {t('billing.subscription.suspendedNotice')}
            </Text>
          </div>
        )}
      </div>

      <Divider style={{ margin: '20px 0' }} />

      {/* Stats Grid */}
      <div className="subscription-stats">
        {/* Plan */}
        <div className="stat-item">
          <div className="stat-icon" style={{ background: '#1890ff' }}>
            <CrownOutlined />
          </div>
          <div className="stat-content">
            <Text type="secondary" className="stat-label">
              {t('billing.subscription.plan')}
            </Text>
            <Text strong className="stat-value">
              {plan && isActive ? plan.name : t('subscription.freePlan')}
            </Text>
          </div>
        </div>

        {/* Subscription Credits */}
        <div className="stat-item">
          <div className="stat-icon" style={{ background: '#faad14' }}>
            <CrownOutlined />
          </div>
          <div className="stat-content">
            <Text type="secondary" className="stat-label">
              {t('billing.subscription.credits')}
            </Text>
            <Text strong className="stat-value">
              {credits.toFixed(1)}
            </Text>
          </div>
        </div>

        {/* Topup Credits */}
        {topupCredits > 0 && (
          <div className="stat-item">
            <div className="stat-icon" style={{ background: '#eb2f96' }}>
              <ThunderboltOutlined />
            </div>
            <div className="stat-content">
              <Text type="secondary" className="stat-label">
                {t('billing.subscription.topupCredits')}
              </Text>
              <Text strong className="stat-value" style={{ color: '#eb2f96' }}>
                {topupCredits.toFixed(1)}
              </Text>
            </div>
          </div>
        )}

        {/* Series Limit */}
        <div className="stat-item">
          <div className="stat-icon" style={{ background: '#722ed1' }}>
            <RocketOutlined />
          </div>
          <div className="stat-content">
            <Text type="secondary" className="stat-label">
              {t('subscription.seriesAllowed')}
            </Text>
            <Text strong className="stat-value">
              {allowedSeriesAmount}
            </Text>
          </div>
        </div>

        {/* Renewal Date */}
        {nextChargeDate && (isActive || isBeforeRenewal) && (
          <div className="stat-item">
            <div className="stat-icon" style={{ background: '#52c41a' }}>
              <CalendarOutlined />
            </div>
            <div className="stat-content">
              <Text type="secondary" className="stat-label">
                {getDateLabel(status)}
              </Text>
              <Text strong className="stat-value">
                {new Date(nextChargeDate).toLocaleDateString()}
              </Text>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default observer(SubscriptionInfo);
